import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LifecycleTypeTab from './LifecycleTypeTab';
import { getExitReasons, addExitReason, updateExitReason, deleteExitReason } from '../../../../../api/Employee/lifecycleSettings';
import {
  DEPARTURE_REASONS,
  ART44_SUB_REASONS,
  departureReasonLabelKey,
} from '../../../../../models/departureReasons';

const ExitReasonsTab: React.FC = () => {
  const { t } = useTranslation();

  // CO-670 — the two option groups, translated once per render rather than per option per card.
  // Group 1 is the seven generic reasons; group 2 the seven named Art. 44 / Art. 80 grounds. The
  // split is the whole point of grouping: picking out of group 2 records a for-cause GROUND, which
  // reads differently on the end-of-service record even though it withholds nothing on its own.
  const eosCategoryGroups = useMemo(() => [
    {
      label: t('Generic reasons'),
      options: DEPARTURE_REASONS.map(r => ({ value: r.value, label: t(r.labelKey) })),
    },
    {
      label: t('Named for-cause grounds (Art. 44 / Art. 80)'),
      options: ART44_SUB_REASONS.map(r => ({ value: r.value, label: t(r.labelKey) })),
    },
  ], [t]);

  return (
    <LifecycleTypeTab
      title={t('Exit Reasons')}
      description={t('Configure reasons for employee exit such as resignation, retirement, and termination')}
      emptyMessage={t('No exit reasons configured yet')}
      emptyIcon="UserMinus"
      addLabel={t('Add Exit Reason')}
      editLabel={t('Edit Exit Reason')}
      deleteLabel={t('Delete Exit Reason')}
      nameLabel={t('Exit reason name')}
      namePlaceholder={t('e.g., Resignation')}
      successAddMessage={t('Exit reason added successfully')}
      successUpdateMessage={t('Exit reason updated successfully')}
      successDeleteMessage={t('Exit reason deleted successfully')}
      failAddMessage={t('Failed to save exit reason')}
      failDeleteMessage={t('Failed to delete exit reason')}
      deleteConfirmMessage={t('Are you sure you want to delete')}
      duplicateMessage={t('An exit reason with this name already exists')}
      fetchItems={getExitReasons}
      addItem={addExitReason}
      updateItem={updateExitReason}
      deleteItem={deleteExitReason}
      testIdPrefix="exit-reason"
      classification={{
        label: t('Classification'),
        // CO-670 split this sentence in two: classification decides the STATUS label, the category
        // below decides the MONEY. They used to be the same decision and no longer are.
        helper: t('Decides whether an employee terminated with this reason is marked Resigned or Terminated. Unspecified falls back to the reason name.'),
        unspecifiedLabel: t('Unspecified'),
        resignationLabel: t('Voluntary (resignation)'),
        terminationLabel: t('Involuntary (termination)'),
        resignationChip: t('Resignation'),
        terminationChip: t('Termination'),
      }}
      eosCategory={{
        label: t('End-of-service category'),
        helper: t('Drives the gratuity rules (resignation factor, Art. 44/80 grounds, notice). Unmapped reasons fall back to the classification above.'),
        unmappedOptionLabel: t('Unmapped'),
        unmappedChipLabel: t('Unmapped — falls back to classification'),
        groups: eosCategoryGroups,
        labelFor: (value: string) => t(departureReasonLabelKey(value)),
      }}
    />
  );
};

export default ExitReasonsTab;
